
import Vue from 'vue'

import { dateHelper } from '@/helpers/dateHelper'
import { OperationUnit, StartEndOf } from '@meltingpoint/lastmile-internationalization'
import CollectService from '@/services/collect/collectService'
import { SnackBarType } from '@/store/types'
import { Collect, Slot } from '@/types'

export default Vue.extend({
  name: 'mp-drive-pickup-phone',
  props: {
    collect: {
      type: Object as () => Collect,
      required: true,
    },
    language: {
      required: true,
    },
  },
  data() {
    return {
      hasLoaded: false,
      month: {
        start: null as Date | null,
        end: null as Date | null,
      },
      selectedDate: null as Date | null,
      slots: [] as Slot[],
      selectedSlot: null as Slot | null,
      dateHelper,
    }
  },
  computed: {
    currentMonthDisplay(): string {
      const monthSplit = dateHelper.format(this.month.start!, 'PPP').split(' ')
      return `${monthSplit[1]} ${monthSplit[2]}`
    },
    fullMonth(): Date[] {
      const current = []

      for (
        let localDay = this.month.start;
        dateHelper.isSameOrBefore(localDay!, this.month.end!);
        localDay = dateHelper.add(localDay!, { days: 1 })
      ) {
        current.push(localDay!)
      }

      return current
    },
  },
  mounted() {
    const now = dateHelper.getDate()

    if (
      this.collect.start &&
      dateHelper.isSameOrAfter(this.collect.start, dateHelper.getDate(), OperationUnit.DAYS)
    ) {
      const beginOfMonth = dateHelper.startOf(this.collect.start, StartEndOf.MONTHS) || null

      if (dateHelper.isBefore(beginOfMonth!, now)) {
        this.month.start = now
      } else {
        this.month.start = beginOfMonth
      }

      this.selectedDate = dateHelper.getDate(this.collect.start)
    } else {
      this.month.start = now
      this.selectedDate = this.month.start
    }

    this.month.end = dateHelper.endOf(this.month.start!, StartEndOf.MONTHS) || null
  },
  methods: {
    getDayFirstLetter(day: Date): string {
      return this.$t(`common.week.${dateHelper.getDayCode(day)}`)[0]
    },
    setSelectedSlot(slot: Slot, slotIndex: number): void {
      this.resetSelected()
      this.selectedSlot = { start: slot.start, end: slot.end }
      this.slots[slotIndex].selected = true
    },
    changeMonth(next = false): void {
      let newStart = null
      let newEnd = null
      if (next) {
        newStart = dateHelper.startOf(
          dateHelper.add(this.month.end!, { days: 1 }),
          StartEndOf.MONTHS,
        )
        newEnd = dateHelper.endOf(newStart as Date, StartEndOf.MONTHS)
      } else {
        newStart = dateHelper.startOf(
          dateHelper.substract(this.month.start!, { days: 1 }),
          StartEndOf.MONTHS,
        )
        newEnd = dateHelper.endOf(newStart as Date, StartEndOf.MONTHS)

        const isCurrentMonth = dateHelper.isDateInInterval(dateHelper.getDate(), {
          start: newStart as Date,
          end: newEnd as Date,
        })

        if (isCurrentMonth) {
          newStart = dateHelper.startOf(this.dateHelper.getDate(), StartEndOf.DAY)!
          newEnd = dateHelper.endOf(newStart as Date, StartEndOf.MONTHS)
        }
      }

      this.month.start = newStart || null
      this.month.end = newEnd || null
      ;(this.$refs.daySelectionWrapper as any).scrollLeft = 0
    },
    resetSelected(): void {
      this.selectedSlot = null

      this.slots = this.slots.map(localSlot => {
        return { ...localSlot, selected: false }
      })
    },
    async generateDay(): Promise<void> {
      const {
        name: type,
        params: { id },
      } = this.$route

      try {
        const { collect, slots } = await CollectService.getSlots(
          type!,
          id,
          this.selectedDate!.toISOString(),
          this.selectedDate!.toISOString(),
          this.language as any || undefined,
        )

        this.slots = []

        for (const slot of slots) {
          let selected = false

          if (
            collect.start &&
            dateHelper.isSame(dateHelper.getDate(collect.start), dateHelper.getDate(slot.start)) &&
            dateHelper.isSame(dateHelper.getDate(collect.end), dateHelper.getDate(slot.end))
          ) {
            selected = true

            this.selectedSlot = {
              start: collect.start,
              end: collect.end,
            }
          }

          this.slots.push({ ...slot, selected })
        }
      } catch (e) {
        this.$store.dispatch('setSnackbar', {
          message: this.$t('messages.error'),
          type: SnackBarType.ERROR,
        })
      } finally {
        this.hasLoaded = true
      }
    },
    async submit() {
      try {
        await CollectService.patchSlot(this.$route.params.id, {
          slot: {
            start: this.selectedSlot!.start as string,
            end: this.selectedSlot!.end as string,
          },
        })

        this.$emit('success')
      } catch (e) {
        this.$store.dispatch('setSnackbar', {
          message: e.message.includes('409')
            ? this.$t('messages.collectSlotConflit')
            : this.$t('messages.error'),
          type: SnackBarType.ERROR,
        })
      }
    },
  },
  watch: {
    selectedDate() {
      this.$nextTick(() => {
        this.generateDay()
      })
    },
  },
})
